import styled from '@emotion/styled';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { Popover, type PopoverProps } from 'antd';
import type { FC, PropsWithChildren, ReactNode } from 'react';
import { IconExclamationTriangle, IconQuestionCircle, IconQuestionOutline } from '../Icons';

interface Props extends PropsWithChildren {
  title?: string;
  placement?: PopoverProps['placement'];
  variant?: 'description' | 'description-light' | 'warning';
  text?: string;
  suffix?: string;
  underlineText?: boolean;
  boldText?: boolean;
  triggerByHover?: 'icon' | 'both';
  width?: number;
  // Only applies to variant="warning" — lets a caller raise the icon to danger red.
  iconColor?: string;
  // Nudges the trigger up and gives it a small left gap, matching HelperModal's HintWrapper —
  // for placing this right after a label instead of relying on the internal Space gap alone.
  elevated?: boolean;
}

export const PopoverHint: FC<Props> = ({
  title,
  children,
  placement,
  variant = 'description-light',
  text = '',
  suffix = '',
  underlineText = false,
  boldText = false,
  triggerByHover = 'icon',
  width,
  elevated = false,
  iconColor,
}) => {
  const icon =
    variant === 'description' ? (
      <IconQuestionCircle />
    ) : variant === 'description-light' ? (
      <IconQuestionOutline />
    ) : (
      <IconExclamationTriangle color={iconColor ?? RacemapColors.Orange} />
    );
  let textPrepared: ReactNode = text;
  if (underlineText) textPrepared = <u>{textPrepared}</u>;
  if (boldText) textPrepared = <strong>{textPrepared}</strong>;

  if (triggerByHover === 'icon') {
    const content = (
      <HintContent>
        {textPrepared}
        <Popover
          trigger={['hover', 'click', 'focus']}
          title={title ? <TitleWrapper>{title}</TitleWrapper> : undefined}
          placement={placement}
          content={<ContentWrapper width={width}>{children}</ContentWrapper>}
          styles={{ content: { padding: 0 }, root: { zIndex: 9999 } }}
        >
          <IconWrapper onClick={(e) => e.stopPropagation()}>{icon}</IconWrapper>
        </Popover>
        {suffix}
      </HintContent>
    );
    return elevated ? <ElevatedWrapper>{content}</ElevatedWrapper> : content;
  }
  const content = (
    <Popover
      trigger={['hover', 'click', 'focus']}
      title={title ? <TitleWrapper>{title}</TitleWrapper> : undefined}
      placement={placement}
      content={<ContentWrapper width={width}>{children}</ContentWrapper>}
      styles={{ content: { padding: 0 }, root: { zIndex: 9999 } }}
    >
      {' '}
      <HintContent>
        {textPrepared}
        <IconWrapper>{icon}</IconWrapper>
        {suffix}
      </HintContent>
    </Popover>
  );
  return elevated ? <ElevatedWrapper>{content}</ElevatedWrapper> : content;
};

// Spans, not antd's Space and a div: those are invalid inside a <p>.
const HintContent = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 3px;
`;

const IconWrapper = styled.span`
  display: inline-flex;
  opacity: 0.7;
  cursor: pointer;
`;

const ElevatedWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 4px;
  transform: translateY(-2px);
`;

const ContentWrapper = styled.div<{ width?: number }>`
  max-width: 300px;
  padding: 12px;
  ${({ width }) => (width ? `min-width: ${width}px;` : '')}
`;

const TitleWrapper = styled.div`
  font-weight: 550;
  color: ${RacemapColors.DarkDarkGray};
  padding: 12px;
  border-bottom: 1px solid ${RacemapColors.Gray};
  background-color: ${RacemapColors.LightLightGray};
`;
